<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { CalendarEvent } from "@/ts/stores/event";
import { EventRegistrationForm } from "@/ts/components/events";
import { dateString, isPastEvent } from "@/ts/utils/event-utils";
import VueEasyLightbox from "vue-easy-lightbox";
import axios from "axios";

var slugify = require("slugify");

interface Props {
    id?: string;
    event: CalendarEvent;
}

const props = defineProps<Props>();
const lightboxIndex = ref<null | number>(null);
const flickrPhotos = ref<string[]>();

/**
 * Computed variables
 */
const isPast = computed<boolean>(() => {
    return isPastEvent(props.event);
});

const images = computed<string[]>(() => {
    let images: string[] = [];
    if (props.event && props.event.acf.media.media_source === "flickr" && flickrPhotos.value) {
        images = flickrPhotos.value;
    } else if (props.event.acf.media.gallery && props.event.acf.media.gallery.length) {
        images = props.event.acf.media.gallery?.map((acfImage) => acfImage.url);
    }
    return images;
});

onMounted(() => {
    if (props.event && props.event.acf.media.media_source === "flickr") {
        axios.get(`/api/flickr-photos?album=${props.event.acf.media.flickr_album_id}`).then((response: { status: number; data: string[] }) => {
            if (response.status === 200 && response.data) {
                flickrPhotos.value = response.data;
            }
        });
    }
});

//console.log(JSON.stringify(props.event, null, 4));
</script>

<template>
    <section :class="isPast ? 'bg-pink' : 'bg-blue'" :id="id">
        <div class="container relative z-10" v-cloak>
            <div class="relative w-full overflow-hidden" :class="{ 'max-h-[70vh]': !event.acf.media.is_poster }">
                <img class="h-auto w-full" :src="event.acf.media.thumbnail.url" :alt="event.title.rendered" />
                <div class="absolute inset-0 flex h-full w-full">
                    <div class="flex flex-1 flex-wrap" :class="{ '-m-3': !isPast }">
                        <div class="hidden w-1/12 p-3 lg:flex" :class="{ '!hidden': isPast }"></div>
                        <div class="flex w-full flex-col items-start justify-end p-6 md:p-9 lg:w-7/12 lg:px-3 lg:py-20" :class="{ 'lg:!px-20 lg:!pb-14': isPast }" v-if="!event.acf.media.is_poster">
                            <div class="mb-4 flex flex-wrap items-center gap-2 md:gap-6">
                                <span class="btn btn-sm border-blue bg-blue text-black" v-html="dateString(event)"></span>
                                <span v-if="isPast" class="btn btn-sm border-black bg-black text-pink">Afgelopen event</span>
                            </div>
                            <h1 class="font-heading text-40 font-[690] uppercase text-white [font-stretch:_190%] md:text-90" v-html="event.title.rendered"></h1>
                        </div>
                    </div>
                </div>
            </div>
            <div class="-m-2 flex flex-wrap md:-m-3">
                <div class="order-2 w-full p-2 md:p-3 lg:order-1" :class="{ 'lg:w-7/12 xl:w-8/12': !isPast }">
                    <div class="flex flex-col bg-white p-6 xl:p-20">
                        <div :class="{ 'order-2 pt-[3.75rem]': isPast }" v-if="event.acf.description" class="prose max-w-none prose-headings:mb-6 prose-headings:font-heading prose-headings:font-[475] prose-headings:[font-stretch:_190%] lg:prose-h1:text-48" v-html="event.acf.description"></div>
                        <div class="-m-2 flex flex-wrap pt-[3.75rem]" v-if="(isPast && event.acf.media.is_poster) || images.length" :class="{ 'order-1 !pt-0': isPast, '-mb-9': !images || (images.length === 0 && isPast) }">
                            <div class="w-full px-2 pt-2" :class="{ 'pb-8': images && images.length > 0 }" v-if="isPast">
                                <template v-if="event.acf.media.is_poster">
                                    <div class="mb-2 flex items-center space-x-6">
                                        <span class="btn btn-sm border-blue bg-blue text-black" v-html="dateString(event)"></span>
                                        <span class="btn btn-sm border-black bg-black text-pink">Afgelopen event</span>
                                    </div>
                                    <h1 class="font-heading text-40 font-[690] uppercase [font-stretch:_190%] md:text-90" :class="{ 'mb-10': images && images.length > 0 }" v-html="event.title.rendered"></h1>
                                </template>
                                <p class="font-heading text-32 font-[475] [font-stretch:_190%] lg:text-48" v-if="images && images.length > 0">Foto's</p>
                            </div>
                            <div class="flex w-full flex-col p-2 md:w-1/2" v-if="images && images.length > 0">
                                <img class="lazyload mb-4 cursor-pointer last:mb-0" @click="lightboxIndex = images.indexOf(img)" v-for="img in images.filter((element, index) => index % 2 === 0)" :data-src="img" />
                            </div>
                            <div class="flex w-full flex-col p-2 md:w-1/2" v-if="images && images.length > 1">
                                <img class="lazyload mb-4 cursor-pointer last:mb-0" @click="lightboxIndex = images.indexOf(img)" v-for="img in images.filter((element, index) => index % 2 !== 0)" :data-src="img" />
                            </div>
                        </div>
                        <div class="mt-[3.75rem]" v-if="event.acf.info.event_type === ('only_registrations' || 'registration_and_payment')">
                            <event-registration-form v-if="event.acf.info.registrations_open" :event="event"></event-registration-form>
                            <p v-else class="font-bold text-20 p-4 bg-gray-300 text-center">Helaas! Online inschrijvingen niet meer mogelijk!</p>
                        </div>
                        <div id="tickets" class="mt-[3.75rem]" v-if="event.acf.info.event_type === 'external_ticketing'">
                            <h2 class="mb-4 font-heading text-40 font-[690] uppercase [font-stretch:_190%] md:mb-4 lg:text-48">Bestel je tickets</h2>
                            <a :href="event.acf.info.external_ticketing_url" target="_blank" rel="noopener" class="btn btn-orange">
                                Tickets kopen
                                <svg-vue icon="ticket" class="ml-3 h-6 w-auto fill-current"></svg-vue>
                            </a>
                        </div>
                    </div>
                </div>
                <div v-if="!isPast" class="order-1 w-full p-2 md:p-3 lg:order-2 lg:w-5/12 xl:w-4/12" :class="{ 'lg:-translate-y-16': !event.acf.media.is_poster && event.acf.info.event_type === 'external_ticketing' }">
                    <a :href="event.acf.info.external_ticketing_url" target="_blank" rel="noopener" class="flex h-16 items-center justify-center bg-orange p-6 text-20 font-semibold uppercase text-white" v-if="event.acf.info.event_type === 'external_ticketing'">
                        Bestel je tickets
                        <svg-vue icon="ticket" class="ml-3 h-6 w-auto"></svg-vue>
                    </a>
                    <div class="flex flex-col items-start bg-white p-6">
                        <div v-if="event.acf.media.is_poster" class="mb-4 flex flex-col items-start space-y-4">
                            <span v-if="event.acf.info.dates.length === 1" class="btn btn-sm border-blue bg-blue text-black" v-html="dateString(event)"></span>
                            <h1 class="font-heading text-32 font-[690] uppercase [font-stretch:_190%] md:text-48" v-html="event.title.rendered"></h1>
                        </div>
                        <div v-if="event.acf.info.pricing && event.acf.info.pricing.length && event.acf.info.pricing[0].price !== ''" class="mb-6 flex flex-col items-start">
                            <span class="mb-2 bg-black py-2 px-3 text-20 font-semibold uppercase text-blue last:mb-0" v-for="price in event.acf.info.pricing">&euro; {{ price.price }} {{ price.group }}</span>
                        </div>
                        <div v-else class="mb-6 flex flex-col items-start">
                            <span class="bg-green py-2 px-3 text-20 font-semibold uppercase text-white">Gratis!</span>
                        </div>
                        <p class="mb-6" v-if="event.acf.info.price_description && event.acf.info.price_description !== ''">{{ event.acf.info.price_description }}</p>
                        <div class="flex w-full flex-col space-y-6 divide-y divide-gray-500">
                            <div class="flex justify-between space-x-4" v-if="event.acf.info.location">
                                <span class="font-semibold text-black">Adres</span>
                                <div class="inline-flex flex-col items-end text-right">
                                    <span v-if="event.acf.info.location.name && event.acf.info.location.name !== '' && event.acf.info.location.name !== event.acf.info.location.street_name + ' ' + event.acf.info.location.street_number">{{ event.acf.info.location.name }}</span>
                                    <span>{{ event.acf.info.location.street_name }} {{ event.acf.info.location.street_number }}</span>
                                    <span>{{ event.acf.info.location.post_code }} {{ event.acf.info.location.city }}</span>
                                </div>
                            </div>
                            <div class="flex flex-col space-y-4 pt-6 first:pt-0" v-for="date in event.acf.info.dates">
                                <span v-if="event.acf.info.dates.length > 1" class="btn btn-sm border-blue bg-blue text-black" v-html="dateString(date.date)"></span>
                                <div class="flex justify-between space-x-4" v-if="date.doors && date.doors !== ''">
                                    <span class="font-semibold text-black">Doors</span>
                                    <div class="inline-flex flex-col items-end">
                                        <span>{{ date.doors }}</span>
                                    </div>
                                </div>
                                <div class="flex justify-between space-x-4" v-if="date.start && date.start !== ''">
                                    <span class="font-semibold text-black">Start</span>
                                    <div class="inline-flex flex-col items-end">
                                        <span>{{ date.start }}</span>
                                    </div>
                                </div>
                                <div class="flex justify-between space-x-4" v-if="date.end && date.end !== ''">
                                    <span class="font-semibold text-black">Einde</span>
                                    <div class="inline-flex flex-col items-end">
                                        <span>{{ date.end }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="pt-6" v-if="event.categories_names && event.categories_names.filter((n) => n !== 'Geen categorie').length">
                                <div class="-m-2 flex flex-wrap">
                                    <div v-for="category in event.categories_names.filter((n) => n !== 'Geen categorie')" class="p-2">
                                        <a :href="`${isPast ? '/galerij' : '/kalender'}?categorie=${slugify(category)}`" class="btn btn-sm border-gray-300 bg-gray-300 text-black transition-colors hover:border-pink hover:bg-pink hover:text-white" v-html="category"></a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <vue-easy-lightbox :move-disabled="true" :loop="true" :visible="lightboxIndex !== null" :imgs="images" :index="lightboxIndex !== null ? lightboxIndex : 0" @hide="lightboxIndex = null">
            <template v-slot:toolbar>
                <!-- hide toolbar -->
            </template>
        </vue-easy-lightbox>
    </section>
</template>

<style lang="scss" scoped>
:deep(.vel-img-wrapper) {
    cursor: default !important;
    transform: translate(-50%, -50%) scale(1) rotate(0deg) !important;
}
</style>
